import React, {ReactNode} from 'react';
import StepModel from "../../../model/configurator/StepModel";
import StepsManager from "./StepsManager";
import FieldCheckboxSelectWithQuantity from "../../configuration/fields/FieldCheckboxSelectWithQuantity";
import FieldRadioSelect from "../../configuration/fields/FieldRadioSelect";
import FieldCheckboxSelect from "../../configuration/fields/FieldCheckboxSelect";
import FieldDisguisedRadioList from "../../configuration/fields/FieldDisguisedRadioList";
import TwsInputMethod from "../../tws-project/calculation/custom-fields/tws-input-method/TwsInputMethod";
import {CustomFieldsEnum} from "../../../enums/CustomFieldsEnum";
import TwsInstallationType from "../../tws-project/calculation/custom-fields/tws-installation-type/TwsInstallationType";
import formModelStore from "../../../store/FormModelStore";
import TwsMatchingUnit from "../../tws-project/calculation/custom-fields/tws-matching-units/TwsMatchingUnit";
import TwsMatchingTapWaterSystem from '../../tws-project/calculation/custom-fields/tws-matching-tap-water-systems/TwsMatchingTapWaterSystem';
import TwsTwsOptions from "../../tws-project/calculation/custom-fields/tws-tws-options/TwsTwsOptions";
import BaseInputValidator from "../../common/input-validators/BaseInputValidator";
import FieldNumber from "../../common/inputs/FieldNumber";
import FieldSelect from "../../common/inputs/FieldSelect";
import FieldText from "../../common/inputs/FieldText";
import Field from "../../../model/configurator/Field";
import FieldBoolean from "../../common/inputs/FieldBoolean";
import {
    TwsProductCatalogSelectProduct
} from "../../tws-project/calculation/custom-fields/tws-product-catalog-select-product/TwsProductCatalogSelectProduct";
import TwsMatchingTankQuantity from '../../tws-project/calculation/custom-fields/tws-matching-tank-quantity/TwsMatchingTankQuantity';
import TwsTankSelectionResult from '../../tws-project/calculation/custom-fields/tws-tank-selection-result/TwsTankSelectionResult';
import {StepsSlugs} from "../../tws-project/calculation/TWSProjectCalculationContainer";
import { updateReactProps } from '../../../ReactUtils';

const fieldTypeMapping: { [key: string]: any } = {
    [CustomFieldsEnum.CHECKBOX]: FieldBoolean,
    [CustomFieldsEnum.RADIO_SELECT]: FieldRadioSelect,
    [CustomFieldsEnum.MULTISELECT]: FieldCheckboxSelect,
    [CustomFieldsEnum.MULTISELECT_WITH_QUANTITY]: FieldCheckboxSelectWithQuantity,
    [CustomFieldsEnum.SELECT]: FieldSelect,
    [CustomFieldsEnum.TEXT]: FieldText,
    [CustomFieldsEnum.NUMBER]: FieldNumber,
    [CustomFieldsEnum.CHECKBOX_RADIO_SELECT]: FieldDisguisedRadioList,
    [CustomFieldsEnum.TWS_INPUT_METHOD]: TwsInputMethod,
    [CustomFieldsEnum.TWS_INSTALLATION_TYPE]: TwsInstallationType,
    [CustomFieldsEnum.TWS_MATCHING_UNIT]: TwsMatchingUnit,
    [CustomFieldsEnum.TWS_MATCHING_TAP_WATER_SYSTEM]: TwsMatchingTapWaterSystem,
    [CustomFieldsEnum.TWS_TWS_OPTIONS]: TwsTwsOptions,
    [CustomFieldsEnum.TWS_PRODUCT_CATALOG_FAMILY]: TwsProductCatalogSelectProduct,
    [CustomFieldsEnum.TWS_MATCHING_TANK_QUANTITY]: TwsMatchingTankQuantity,
    [CustomFieldsEnum.TWS_TANK_SELECTION_RESULT]: TwsTankSelectionResult
}

export enum EditorType {
    Responsible = 0,
    Participant = 1,
    Viewer = 2
}

interface SelfProps {
    editMode: boolean,
    editorType: EditorType,
    model: any,
    visible: boolean,
    stepsManager: StepsManager,
    onFormModelChange: any,
    setDirty: () => void;
    stepChangeCallback: (stepSlug: string, canProceed: boolean) => void;
    setLastAvailableStep: (stepSlug: string) => void;
    updateStepAvailability: (stepSlug: StepsSlugs, available: boolean) => void;
    stepSlug: string,
}

export default class BaseStep<T extends typeof BaseStep = typeof BaseStep> extends React.Component<SelfProps> {
    stepModel: StepModel | null;
    fields: { [key: string]: Field } = {};

    constructor(props: SelfProps) {
        super(props)

        this.stepModel = (this.constructor as T).getStepModel(props.model)
        if (this.stepModel !== null) {
            this.fields = (this.constructor as T).getFields(this.stepModel)
            let firstVisibleField = (this.constructor as T).getFirstVisibleField(this.fields);
            let lastVisibleField = (this.constructor as T).getLastVisibleField(this.fields);
            this.stepModel.initialize(this.fields, firstVisibleField, lastVisibleField);
        }
    }

    componentDidMount() {
        if (!this.props.editMode)
            return;

        this.onEditMode();
    }

    render() {
        return (
            <React.Fragment>
                {this.renderBody(this.props.model)}
            </React.Fragment>
        )
    }

    renderBody(model: any): ReactNode {
        throw new Error('NotImplementedError: Step#renderBody.')
    }

    private setReadonly(): void {
        if (this.canEdit())
            return;

        Object.keys(this.fields).forEach((key, index) => {
            (this.fields[key] as Field).readonly = true;
        });
    }

    protected onEditMode(): void {
        this.setReadonly();
    }

    public canEdit(): boolean {
        return this.props.editMode == true && this.props.editorType != EditorType.Viewer;
    }

    onFormModelChange() {

    }

    renderField = (field: any, additionalProps: any = {}, validator: BaseInputValidator | undefined = undefined) => {
        if (field === undefined || field.visible === false) {
            return <React.Fragment></React.Fragment>
        }

        const FieldComponent = fieldTypeMapping[additionalProps.type ?? field.type]
        if (FieldComponent) {
            const changeCallback = (newValue: any) => {
                let shouldUpdateStore = true

                if (field.onChange) {
                    shouldUpdateStore = field.onChange(newValue)

                    if (shouldUpdateStore === undefined) {
                        shouldUpdateStore = true
                    }
                }

                formModelStore.setRecentlyChangedField(field.getModelPath());
                // Specifying undefined as a value will make JS remove the property completely,
                // which will cause problems during transformation.
                if (shouldUpdateStore && newValue !== undefined) {
                    field.value = newValue
                }


                if (this.onFormModelChange) {
                    this.onFormModelChange()
                }
            }

            return (
                <FieldComponent
                    ref={(element: any) => field.setRef(element)}
                    onChange={changeCallback}
                    step={this.stepModel}
                    field={field}
                    validator={validator}
                    {...(field.getComponentProperties())}
                    {...additionalProps}
                    unitLabel={field.unit}
                />
            )
        }

        console.warn('Component for field type: ' + field.type + ' was not found on field: ', field)

        return <React.Fragment></React.Fragment>
    }

    /**
     *
     * @param step {StepModel}
     * @returns {{}}
     */
    static getFields(step: StepModel) {
        return {}
    }

    static getFirstVisibleField(fields: any): StepModel | null {
        return null
    }

    static getLastVisibleField(fields: any): StepModel | null {
        return null
    }

    /**
     *
     * @param model
     * @returns {StepModel | null}
     */
    static getStepModel(model: any): StepModel | null {
        return null
    }
}